import { useLazyQuery, useMutation } from '@apollo/client';
import {
  DndContext,
  DragEndEvent,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { FC, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { Button, Input, Spinner } from 'reactstrap';
import {
  ADD_INTERVIEW_QUESTION,
  DELETE_INTERVIEW_QUESTION,
  GENERATE_INTERVIEW_QUESTIONS,
  GET_INTERVIEW_QUESTIONS,
  REORDER_INTERVIEW_QUESTIONS,
  RESTORE_STARTER_QUESTIONS,
  UPDATE_INTERVIEW_QUESTION,
  UPDATE_INTERVIEW_QUESTION_POSITION,
} from '../gql';

const DIFFICULTY_LABELS: Record<number, string> = {
  1: 'Entry Level', 2: 'Entry Level',
  3: 'Standard',   4: 'Standard',
  5: 'Intermediate', 6: 'Intermediate',
  7: 'Advanced',   8: 'Advanced',
  9: 'Expert',     10: 'Expert',
};

const AMBER = '#eaa827';
const AMBER_DARK = '#c88e1e';

// Wire values match the GraphQL `InterviewQuestionSource` enum members (uppercase).
type InterviewQuestionSource = 'STARTER' | 'AI' | 'MANUAL';

// Wire values match the GraphQL `InterviewQuestionPosition` enum members (uppercase).
type InterviewQuestionPosition = 'OPENING' | 'CLOSING';

interface InterviewQuestion {
  id: string;
  jobOrderId: string;
  question: string;
  difficulty: number;
  sortOrder: number;
  source: InterviewQuestionSource;
  starterKey?: number | null;
  isRemoved: boolean;
  position: InterviewQuestionPosition | null;
}

// ── Source badge ──────────────────────────────────────────────────────────────
const SourceBadge: FC<{ source: InterviewQuestionSource }> = ({ source }) => {
  if (source === 'STARTER') {
    return (
      <span style={{
        display: 'inline-block', fontSize: 11, fontWeight: 600, padding: '2px 8px',
        borderRadius: 10, background: '#e7f1ff', color: '#0d6efd', border: '1px solid #b6d4fe',
        marginBottom: 6,
      }}>
        Starter
      </span>
    );
  }
  if (source === 'AI') {
    return (
      <span style={{
        display: 'inline-block', fontSize: 11, fontWeight: 600, padding: '2px 8px',
        borderRadius: 10, background: '#fff3cd', color: '#856404', border: '1px solid #ffe69c',
        marginBottom: 6,
      }}>
        AI
      </span>
    );
  }
  return null;
};

// ── Drag handle icon ──────────────────────────────────────────────────────────
const DragHandle: FC<{ listeners: any; attributes: any }> = ({ listeners, attributes }) => (
  <div
    {...listeners}
    {...attributes}
    title="Drag to reorder"
    style={{
      cursor: 'grab',
      color: '#adb5bd',
      padding: '2px 4px',
      fontSize: 15,
      lineHeight: 1,
      userSelect: 'none',
      flexShrink: 0,
      alignSelf: 'center',
    }}
  >
    ⠿
  </div>
);

// ── Single sortable row ───────────────────────────────────────────────────────
interface RowProps {
  question: InterviewQuestion;
  index: number;
  readOnly: boolean;
  editingId: string | null;
  editingText: string;
  onEditStart: (id: string, text: string) => void;
  onEditChange: (text: string) => void;
  onEditSave: (id: string) => void;
  onEditCancel: () => void;
  onDelete: (question: InterviewQuestion) => void;
  // Starter-only (D3): AI and manual rows never receive this prop's button.
  onTogglePosition: (question: InterviewQuestion) => void;
  togglingId: string | null;
}

const SortableRow: FC<RowProps> = ({
  question: q,
  index,
  readOnly,
  editingId,
  editingText,
  onEditStart,
  onEditChange,
  onEditSave,
  onEditCancel,
  onDelete,
  onTogglePosition,
  togglingId,
}) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: q.id,
    disabled: readOnly,
  });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    display: 'flex',
    alignItems: 'flex-start',
    gap: 8,
    marginBottom: 6,
    padding: '6px 10px',
    border: `1px solid ${isDragging ? AMBER : '#dee2e6'}`,
    borderRadius: 6,
    background: isDragging ? '#fffbf0' : '#fff',
    boxShadow: isDragging ? '0 4px 12px rgba(0,0,0,0.12)' : 'none',
    zIndex: isDragging ? 999 : 'auto',
    position: 'relative',
  };

  return (
    <div ref={setNodeRef} style={style}>
      {/* Drag handle */}
      {!readOnly && <DragHandle listeners={listeners} attributes={attributes} />}

      {/* Order number */}
      <span style={{ minWidth: 22, fontSize: 13, fontWeight: 600, color: '#6c757d', paddingTop: 1, flexShrink: 0 }}>
        {index + 1}.
      </span>

      {/* Question text / edit textarea */}
      <div style={{ flex: 1 }}>
        {q.source !== 'MANUAL' && (
          <div>
            <SourceBadge source={q.source} />
          </div>
        )}
        {editingId === q.id ? (
          <textarea
            value={editingText}
            onChange={(e) => onEditChange(e.target.value)}
            rows={3}
            style={{
              width: '100%',
              border: '1px solid #80bdff',
              borderRadius: 4,
              padding: '6px 8px',
              fontSize: 13,
              resize: 'vertical',
            }}
            autoFocus
          />
        ) : (
          <span style={{ fontSize: 13, lineHeight: 1.4 }}>{q.question}</span>
        )}
      </div>

      {/* Action buttons */}
      {!readOnly && (
        <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
          {editingId === q.id ? (
            <>
              <Button size="sm" color="success" onClick={() => onEditSave(q.id)}>Save</Button>
              <Button size="sm" color="secondary" onClick={onEditCancel}>Cancel</Button>
            </>
          ) : (
            <>
              {/* Starter-only (D3): moves this question between the Opening and
                  Closing sections by writing the per-job-order position override. */}
              {q.source === 'STARTER' && (
                <Button
                  size="sm"
                  color="outline-primary"
                  disabled={togglingId === q.id}
                  onClick={() => onTogglePosition(q)}
                  title={
                    q.position === 'CLOSING'
                      ? 'Move this question to the Opening section'
                      : 'Move this question to the Closing section'
                  }
                >
                  {togglingId === q.id ? (
                    <Spinner size="sm" />
                  ) : q.position === 'CLOSING' ? (
                    'Move to Opening'
                  ) : (
                    'Move to Closing'
                  )}
                </Button>
              )}
              <Button size="sm" color="outline-secondary" onClick={() => onEditStart(q.id, q.question)}>
                Edit
              </Button>
              <Button size="sm" color="outline-danger" onClick={() => onDelete(q)}>✕</Button>
            </>
          )}
        </div>
      )}
    </div>
  );
};

// ── Main tab component ────────────────────────────────────────────────────────
interface Props {
  jobOrderId: string;
  advertisedJobDescription?: string;
  readOnly?: boolean;
}

const InterviewQuestionsTab: FC<Props> = ({ jobOrderId, advertisedJobDescription, readOnly = false }) => {
  const [difficulty, setDifficulty] = useState(5);
  const [count, setCount] = useState(3);
  const [questions, setQuestions] = useState<InterviewQuestion[]>([]);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingText, setEditingText] = useState('');
  const [addingNew, setAddingNew] = useState(false);
  const [newQuestionText, setNewQuestionText] = useState('');

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
  );

  const [fetchQuestions, { loading: loadingFetch }] = useLazyQuery(GET_INTERVIEW_QUESTIONS, {
    fetchPolicy: 'cache-and-network',
    onCompleted: (data) => setQuestions(data?.getInterviewQuestions ?? []),
  });

  const [generateQuestions, { loading: generating }] = useMutation(GENERATE_INTERVIEW_QUESTIONS, {
    onCompleted: (data) => {
      setQuestions(data?.generateInterviewQuestions ?? []);
      toast.success('Interview questions generated successfully');
    },
    onError: (err) => toast.error(err.message),
  });

  const [updateQuestion] = useMutation(UPDATE_INTERVIEW_QUESTION, {
    onCompleted: (data) => {
      setQuestions((prev) =>
        prev.map((q) => (q.id === data.updateInterviewQuestion.id ? { ...q, ...data.updateInterviewQuestion } : q)),
      );
      setEditingId(null);
    },
    onError: (err) => toast.error(err.message),
  });

  const [addQuestion] = useMutation(ADD_INTERVIEW_QUESTION, {
    onCompleted: (data) => {
      setQuestions((prev) => [...prev, data.addInterviewQuestion]);
      setAddingNew(false);
      setNewQuestionText('');
    },
    onError: (err) => toast.error(err.message),
  });

  const [deleteQuestion] = useMutation(DELETE_INTERVIEW_QUESTION, {
    onError: (err) => toast.error(err.message),
  });

  const [reorderQuestions] = useMutation(REORDER_INTERVIEW_QUESTIONS, {
    onCompleted: (data) => {
      // Only active (non-removed) ids are sent for reordering, so the response covers the
      // active list only — removed starters are preserved from local state alongside it.
      setQuestions((prev) => {
        const removed = prev.filter((q) => q.isRemoved);
        return [...(data?.reorderInterviewQuestions ?? []), ...removed];
      });
    },
    onError: (err) => toast.error(err.message),
  });

  const [restoreAllStarters, { loading: restoringAll }] = useMutation(RESTORE_STARTER_QUESTIONS, {
    onCompleted: (data) => {
      setQuestions(data?.restoreStarterQuestions ?? []);
      toast.success('Starter questions restored');
    },
    onError: (err) => toast.error(err.message),
  });

  const [togglingId, setTogglingId] = useState<string | null>(null);
  const [updatePosition] = useMutation(UPDATE_INTERVIEW_QUESTION_POSITION, {
    onCompleted: (data) => {
      setQuestions((prev) =>
        prev.map((q) =>
          q.id === data.updateInterviewQuestionPosition.id
            ? { ...q, ...data.updateInterviewQuestionPosition }
            : q,
        ),
      );
      setTogglingId(null);
    },
    onError: (err) => {
      toast.error(err.message);
      setTogglingId(null);
    },
  });

  useEffect(() => {
    if (jobOrderId) fetchQuestions({ variables: { jobOrderId } });
  }, [jobOrderId]);

  const activeQuestions = questions.filter((q) => !q.isRemoved);
  const removedStarters = questions.filter((q) => q.isRemoved);

  // Three buckets mirroring the real RetellAI send order (the API orders the same
  // way in SQL — opening starters, then AI/manual, then closing starters). A
  // STARTER row with a NULL position counts as OPENING, defensively: it must never
  // disappear from the tab or jump to the closing section. Every active question
  // appears in exactly one of these three lists.
  const isStarter = (q: InterviewQuestion) => q.source === 'STARTER';
  const activeOpening = activeQuestions.filter((q) => isStarter(q) && q.position !== 'CLOSING');
  const activeMiddle = activeQuestions.filter((q) => !isStarter(q));
  const activeClosing = activeQuestions.filter((q) => isStarter(q) && q.position === 'CLOSING');

  // Drag is scoped within a section (three isolated DndContexts). Whichever
  // section moved, we rebuild the full active list in [opening, middle, closing]
  // order — the same concatenation the AI recruiter voice is sent — and persist
  // the flattened order so sortOrder stays consistent with send order.
  const buildOrdered = (opening: InterviewQuestion[], middle: InterviewQuestion[], closing: InterviewQuestion[]) => [
    ...opening,
    ...middle,
    ...closing,
  ];

  const makeSectionDragEnd =
    (sectionItems: InterviewQuestion[], buildActive: (reordered: InterviewQuestion[]) => InterviewQuestion[]) =>
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;

      const oldIndex = sectionItems.findIndex((q) => q.id === active.id);
      const newIndex = sectionItems.findIndex((q) => q.id === over.id);
      const reordered = arrayMove(sectionItems, oldIndex, newIndex);
      const newActive = buildActive(reordered);
      setQuestions([...newActive, ...removedStarters]);
      reorderQuestions({ variables: { jobOrderId, orderedIds: newActive.map((q) => q.id) } });
    };

  const onOpeningDragEnd = makeSectionDragEnd(activeOpening, (reordered) =>
    buildOrdered(reordered, activeMiddle, activeClosing),
  );
  const onMiddleDragEnd = makeSectionDragEnd(activeMiddle, (reordered) =>
    buildOrdered(activeOpening, reordered, activeClosing),
  );
  const onClosingDragEnd = makeSectionDragEnd(activeClosing, (reordered) =>
    buildOrdered(activeOpening, activeMiddle, reordered),
  );

  const handleTogglePosition = (question: InterviewQuestion) => {
    const nextPosition: InterviewQuestionPosition = question.position === 'CLOSING' ? 'OPENING' : 'CLOSING';
    setTogglingId(question.id);
    updatePosition({ variables: { id: question.id, position: nextPosition } });
  };

  const handleDelete = async (question: InterviewQuestion) => {
    const isStarter = question.source === 'STARTER';
    await deleteQuestion({ variables: { id: question.id } });
    if (isStarter) {
      setQuestions((prev) => prev.map((q) => (q.id === question.id ? { ...q, isRemoved: true } : q)));
    } else {
      setQuestions((prev) => prev.filter((q) => q.id !== question.id));
    }
  };

  const hasStarters = questions.some((q) => q.source === 'STARTER');
  const starterCount = activeOpening.length + activeClosing.length;

  const handleRestoreAll = () => {
    const message = hasStarters
      ? `Reset all ${starterCount} starter questions to their default wording, order and position? Edits to starter questions — including any opening/closing changes — will be lost. Your AI/manual questions are kept.`
      : 'Add the default starter questions to this job order?';
    if (!window.confirm(message)) return;
    restoreAllStarters({ variables: { jobOrderId } });
  };

  const noDescription = !advertisedJobDescription;

  const editHandlers = {
    editingId,
    editingText,
    onEditStart: (id: string, text: string) => { setEditingId(id); setEditingText(text); },
    onEditChange: setEditingText,
    onEditSave: (id: string) => {
      if (!editingText.trim()) return;
      updateQuestion({ variables: { id, question: editingText.trim() } });
    },
    onEditCancel: () => setEditingId(null),
    onDelete: handleDelete,
  };

  const renderSection = (
    items: InterviewQuestion[],
    indexOffset: number,
    onDragEnd: (event: DragEndEvent) => void,
  ) => (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
      <SortableContext items={items.map((q) => q.id)} strategy={verticalListSortingStrategy}>
        {items.map((q, index) => (
          <SortableRow
            key={q.id}
            question={q}
            index={indexOffset + index}
            readOnly={readOnly}
            onTogglePosition={handleTogglePosition}
            togglingId={togglingId}
            {...editHandlers}
          />
        ))}
      </SortableContext>
    </DndContext>
  );

  const sectionHeader = (title: string, subtitle: string) => (
    <div style={{ marginBottom: 8 }}>
      <div style={{ fontWeight: 700, fontSize: 14, color: '#212529' }}>{title}</div>
      <div style={{ fontSize: 11, color: '#6c757d' }}>{subtitle}</div>
    </div>
  );

  return (
    <div style={{ padding: '20px' }}>
      {loadingFetch ? (
        <div style={{ textAlign: 'center', padding: 40 }}><Spinner /></div>
      ) : (
        <>
          {/* These three sections are shown in the exact order the AI recruiter
              voice (RetellAI) asks them: opening starters, then the job-specific
              AI/manual questions, then closing starters. Reordering within a
              section is reflected in that same send order. */}

          {/* ═══ 1. Opening questions ═══════════════════════════════════════ */}
          <section style={{ marginBottom: 32 }}>
            {sectionHeader('Opening Questions', 'Asked first, before the job-specific questions.')}

            {activeOpening.length === 0 && (
              <p style={{ color: '#6c757d', fontStyle: 'italic' }}>
                No opening starter questions on this job order.
                {!readOnly && ' Use the button below to add the default set.'}
              </p>
            )}

            {renderSection(activeOpening, 0, onOpeningDragEnd)}
          </section>

          {/* ═══ 2. AI & additional questions ══════════════════════════════ */}
          <section style={{ marginBottom: 32 }}>
            {sectionHeader('AI & Additional Questions', 'Generated or manually added — asked after the opening questions.')}

            {/* Generate controls — hidden in read-only mode */}
            {!readOnly && (
              <div style={{ marginBottom: 20, padding: '16px', background: '#f8f9fa', borderRadius: 8 }}>
                {/* Difficulty picker */}
                <div style={{ marginBottom: 16 }}>
                  <div style={{ fontWeight: 700, marginBottom: 4 }}>
                    Interview Difficulty{' '}
                    <span style={{ fontWeight: 400 }}>{difficulty} — {DIFFICULTY_LABELS[difficulty]}</span>
                  </div>
                  <div style={{ fontSize: 12, color: '#6c757d', marginBottom: 10 }}>
                    Calibrates how tough the AI-generated questions will be. Match to the role's seniority.
                  </div>
                  <div style={{ display: 'flex', gap: 6 }}>
                    {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => {
                      const filled = n <= difficulty;
                      return (
                        <button
                          key={n}
                          onClick={() => setDifficulty(n)}
                          style={{
                            width: 38, height: 38, borderRadius: 6,
                            border: `1px solid ${filled ? AMBER : '#ced4da'}`,
                            background: filled ? AMBER : '#fff',
                            color: filled ? '#fff' : '#495057',
                            fontWeight: 600, fontSize: 14, cursor: 'pointer',
                            transition: 'background 0.15s, border-color 0.15s',
                          }}
                          onMouseEnter={(e) => {
                            if (!filled) {
                              (e.currentTarget as HTMLButtonElement).style.borderColor = AMBER;
                              (e.currentTarget as HTMLButtonElement).style.color = AMBER_DARK;
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (!filled) {
                              (e.currentTarget as HTMLButtonElement).style.borderColor = '#ced4da';
                              (e.currentTarget as HTMLButtonElement).style.color = '#495057';
                            }
                          }}
                        >
                          {n}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Count + Generate row */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <label style={{ margin: 0, fontWeight: 500, whiteSpace: 'nowrap' }}># Questions:</label>
                    <Input
                      type="number" min={1} max={10} value={count}
                      onChange={(e) => setCount(Math.min(10, Math.max(1, parseInt(e.target.value) || 1)))}
                      style={{ width: 70 }}
                    />
                  </div>
                  <Button
                    color="primary" onClick={() => generateQuestions({ variables: { jobOrderId, difficulty, count } })}
                    disabled={generating || noDescription} style={{ whiteSpace: 'nowrap' }}
                  >
                    {generating ? <><Spinner size="sm" className="mr-2" /> Generating...</> : 'Generate Questions'}
                  </Button>
                  {noDescription && (
                    <span style={{ color: '#dc3545', fontSize: 13 }}>
                      No job description found — add one to the About tab first.
                    </span>
                  )}
                </div>
              </div>
            )}

            {activeMiddle.length === 0 && !addingNew && (
              <p style={{ color: '#6c757d', fontStyle: 'italic' }}>
                No AI or manual questions yet.{!readOnly && ' Generate some above or add them manually.'}
              </p>
            )}

            {renderSection(activeMiddle, activeOpening.length, onMiddleDragEnd)}

            {/* New question row */}
            {!readOnly && addingNew && (
              <div style={{
                display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 6,
                padding: '6px 10px', border: '1px dashed #80bdff', borderRadius: 6, background: '#f0f7ff',
              }}>
                <span style={{ minWidth: 22, fontSize: 13, fontWeight: 600, color: '#6c757d', paddingTop: 1 }}>
                  {activeOpening.length + activeMiddle.length + 1}.
                </span>
                <div style={{ flex: 1 }}>
                  <textarea
                    value={newQuestionText}
                    onChange={(e) => setNewQuestionText(e.target.value)}
                    placeholder="Type your question here..."
                    rows={2}
                    style={{ width: '100%', border: '1px solid #80bdff', borderRadius: 4, padding: '6px 8px', fontSize: 13, resize: 'vertical' }}
                    autoFocus
                  />
                </div>
                <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                  <Button size="sm" color="success" onClick={() => {
                    if (!newQuestionText.trim()) return;
                    addQuestion({ variables: { jobOrderId, question: newQuestionText.trim(), difficulty } });
                  }}>
                    Save
                  </Button>
                  <Button size="sm" color="secondary" onClick={() => { setAddingNew(false); setNewQuestionText(''); }}>
                    Cancel
                  </Button>
                </div>
              </div>
            )}

            {!readOnly && !addingNew && (
              <div style={{ marginTop: 8 }}>
                <Button color="outline-primary" size="sm" onClick={() => setAddingNew(true)}>
                  + Add Question
                </Button>
              </div>
            )}
          </section>

          {/* ═══ 3. Closing questions ═══════════════════════════════════════ */}
          <section style={{ marginBottom: 16 }}>
            {sectionHeader('Closing Questions', 'Asked last, after the job-specific questions.')}

            {activeClosing.length === 0 && (
              <p style={{ color: '#6c757d', fontStyle: 'italic' }}>
                No closing starter questions on this job order.
              </p>
            )}

            {renderSection(activeClosing, activeOpening.length + activeMiddle.length, onClosingDragEnd)}
          </section>

          {/* Restores both the opening and closing starter sections at once —
              it resets wording, order, and position together, so it lives below
              both rather than implying it only affects one. */}
          {!readOnly && (
            <div style={{ marginBottom: 8 }}>
              <Button
                color="outline-secondary"
                size="sm"
                disabled={restoringAll}
                onClick={handleRestoreAll}
                title="Bring back the default starter questions (resets any starter edits, order and position)"
              >
                {restoringAll ? (
                  <><Spinner size="sm" className="mr-2" /> Restoring...</>
                ) : hasStarters ? (
                  'Restore starter questions'
                ) : (
                  'Add starter questions'
                )}
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default InterviewQuestionsTab;
